import React, { useState } from 'react';
import { useEngagementAnalytics } from '../hooks/useAnalytics';
import { AnalyticsFilters } from '../types/analytics.types';
import { KPICard } from './KPICard';
import { ActiveUsersChart } from './ActiveUsersChart';
import { SessionMetrics } from './SessionMetrics';
import { RetentionMetrics } from './RetentionMetrics';
import './EngagementTab.css';

interface EngagementTabProps {
  filters: AnalyticsFilters;
  onFiltersChange: (filters: AnalyticsFilters) => void;
}

export const EngagementTab: React.FC<EngagementTabProps> = ({ filters, onFiltersChange }) => {
  const { data, loading, error, refetch } = useEngagementAnalytics(filters);
  const [granularity, setGranularity] = useState<'daily' | 'weekly' | 'monthly'>(
    filters.granularity || 'daily'
  );

  const handleGranularityChange = (newGranularity: 'daily' | 'weekly' | 'monthly') => {
    setGranularity(newGranularity);
    onFiltersChange({
      ...filters,
      granularity: newGranularity
    });
  };

  if (error) {
    return (
      <div className="engagement-tab">
        <div className="error-message">
          <h3>❌ Error Loading Engagement Data</h3>
          <p>{error.message}</p>
          <button onClick={() => refetch()} className="retry-button">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="engagement-tab">
      {/* Summary KPIs */}
      <div className="engagement-summary">
        <h2>Engagement Overview</h2>
        <div className="kpi-grid">
          {loading ? (
            <>
              <KPICard title="Total Users" value="0" loading={true} />
              <KPICard title="Avg Daily Active" value="0" loading={true} />
              <KPICard title="Total Sessions" value="0" loading={true} />
              <KPICard title="Avg Actions" value="0" loading={true} />
            </>
          ) : data?.summary ? (
            <>
              <KPICard
                title="Total Users"
                value={data.summary.total_unique_users.toLocaleString()}
                subtitle="Unique learners"
                icon="👥"
              />
              <KPICard
                title="Avg Daily Active"
                value={Math.round(data.summary.avg_daily_active_users).toLocaleString()}
                subtitle="Users per day"
                icon="📊"
              />
              <KPICard
                title="Total Sessions"
                value={data.sessions?.total_sessions.toLocaleString() || '0'}
                subtitle={`${data.summary.date_range_days} days`}
                icon="🔄"
              />
              <KPICard
                title="Avg Actions/User"
                value={data.summary.avg_actions_per_user.toFixed(1)}
                subtitle="Per user engagement"
                icon="⚡"
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Active Users Chart */}
      <div className="engagement-section">
        <div className="section-header">
          <h3>Active Users Trend</h3>
          <div className="granularity-selector">
            <button
              className={granularity === 'daily' ? 'active' : ''}
              onClick={() => handleGranularityChange('daily')}
            >
              Daily
            </button>
            <button
              className={granularity === 'weekly' ? 'active' : ''}
              onClick={() => handleGranularityChange('weekly')}
            >
              Weekly
            </button>
            <button
              className={granularity === 'monthly' ? 'active' : ''}
              onClick={() => handleGranularityChange('monthly')}
            >
              Monthly
            </button>
          </div>
        </div>
        {data?.active_users && (
          <ActiveUsersChart data={data.active_users} loading={loading} />
        )}
      </div>

      {/* Session Metrics */}
      <div className="engagement-section">
        <h3>Session Metrics</h3>
        {data?.sessions && (
          <SessionMetrics data={data.sessions} loading={loading} />
        )}
      </div>

      {/* Retention Analysis */}
      <div className="engagement-section">
        {data?.retention && (
          <RetentionMetrics data={data.retention} loading={loading} />
        )}
      </div>

      {/* Additional Insights */}
      {data?.summary && !loading && (
        <div className="engagement-insights">
          <h3>💡 Key Insights</h3>
          <div className="insights-grid">
            <div className="insight-card">
              <span className="insight-icon">📈</span>
              <div className="insight-content">
                <h4>User Activity</h4>
                <p>
                  {data.summary.total_unique_users.toLocaleString()} users generated{' '}
                  {data.summary.total_actions.toLocaleString()} learning actions over{' '}
                  {data.summary.date_range_days} days
                </p>
              </div>
            </div>

            {data.sessions && (
              <div className="insight-card">
                <span className="insight-icon">⏱️</span>
                <div className="insight-content">
                  <h4>Session Quality</h4>
                  <p>
                    Average session lasts {data.sessions.avg_session_duration_minutes.toFixed(1)}{' '}
                    minutes with {data.sessions.avg_actions_per_session.toFixed(1)} actions per session
                  </p>
                </div>
              </div>
            )}

            {data.retention?.retention_rates.day_7 && (
              <div className="insight-card">
                <span className="insight-icon">🎯</span>
                <div className="insight-content">
                  <h4>7-Day Retention</h4>
                  <p>
                    {data.retention.retention_rates.day_7.retention_rate.toFixed(1)}% of users return
                    after 7 days ({data.retention.retention_rates.day_7.retained_users} users)
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EngagementTab;
